import { ToastType } from '@components/toast';
import { SystemAlertPostRequestBody } from '@lio/api-types';
import { alertsQueryKeys } from '@services/alert/keys';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { useClient } from '@utils/api-client';
import { getApiErrorMessage } from '@utils/getApiErrorMessage';
import { useSnackbar } from 'notistack';

export const useCreateSystemAlert = () => {
  const client = useClient();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const mutationFn = async (alertData: SystemAlertPostRequestBody) => {
    if (alertData.startAt && alertData.endAt && alertData.startAt >= alertData.endAt) {
      throw new Error('startAt must be less than endAt');
    }

    return client.post(`system-management/system-alert/`, {
      json: {
        ...alertData,
      },
    });
  };

  const result = useMutation({
    mutationFn,
    onError: async (error) => {
      const fallback = error instanceof Error ? error.message : 'An error occurred.';
      const message = await getApiErrorMessage(error, fallback);
      enqueueSnackbar(`System Alert Creation Failed: ${message}`, {
        variant: ToastType.Error,
        persist: true,
      });
    },
    onSuccess: (_, __) => {
      queryClient.invalidateQueries({ queryKey: [...alertsQueryKeys.all] }).then(() => {
        queryClient.fetchQuery({ queryKey: ['alerts', { activeAt: 'now' }] });
        enqueueSnackbar('System Alert Successfully Created', {
          variant: ToastType.Success,
        });
      });
    },
  });

  return { ...result, createSystemAlertAsync: result.mutateAsync };
};
